import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ArrowUpRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";
import { FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";

function fmtDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function News() {
  const [tag, setTag] = useState("All");
  const { data: posts, isLoading } = useQuery({
    queryKey: ["posts"],
    queryFn: async () => (await api.get("/posts")).data,
  });

  const tags = ["All", ...new Set((posts || []).map((p) => p.tag))];
  const shown = (posts || []).filter((p) => tag === "All" || p.tag === tag);

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-32 sm:px-8 lg:px-14 lg:pt-40">
      <Seo
        title="News & Notes | Isabella MacLeod"
        description="News, essays, and behind-the-book notes from Isabella MacLeod — author of the Miranda trilogy: Metamorphosis, Nano, and Transhuman."
        path="/news"
      />
      <FadeUp className="mb-14 max-w-3xl">
        <Eyebrow className="mb-6">The Desk</Eyebrow>
        <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
          News &amp; <span className="serif-italic text-cyanx">notes</span>
        </h1>
        <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-dim sm:text-lg">
          Book news, research and advocacy essays, and behind-the-book notes —
          straight from Nova Scotia.
        </p>
      </FadeUp>

      {isLoading && (
        <div className="flex items-center gap-3 py-16 font-mono text-xs uppercase tracking-[0.3em] text-mute2">
          <Loader2 size={14} className="animate-spin" /> Loading the desk…
        </div>
      )}

      {posts && posts.length > 0 && (
        <FadeUp className="mb-12 flex flex-wrap gap-2">
          {tags.map((t) => (
            <button
              key={t}
              onClick={() => setTag(t)}
              data-testid={`news-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                tag === t
                  ? "border-cyanx bg-cyanx text-void"
                  : "border-white/15 text-dim hover:border-cyanx/50 hover:text-cyanx"
              }`}
            >
              {t}
            </button>
          ))}
        </FadeUp>
      )}

      <div>
        {shown.map((p, i) => (
          <FadeUp key={p.slug} testid={`news-post-${p.slug}`}>
            <article className="grid gap-4 border-t border-white/[0.07] py-10 md:grid-cols-12 md:gap-8">
              <div className="md:col-span-3">
                <p className="font-mono text-[11px] tracking-[0.2em] text-mute2">
                  {fmtDate(p.published_at)}
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ember">
                  {p.tag}
                </p>
              </div>
              <div className="md:col-span-9">
                <h2 className="max-w-2xl font-serif text-3xl font-light leading-tight text-ink sm:text-4xl">
                  <Link
                    to={`/news/${p.slug}`}
                    className="transition-colors hover:text-cyanx"
                    data-testid={`news-link-${p.slug}`}
                  >
                    {p.title}
                  </Link>
                </h2>
                <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-dim">
                  {p.excerpt}
                </p>
                <Link
                  to={`/news/${p.slug}`}
                  data-testid={`news-read-${p.slug}`}
                  className="group mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-cyanx"
                >
                  Read the note
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>

      {posts && posts.length === 0 && !isLoading && (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-mute2">
          Nothing on the desk yet — check back soon.
        </p>
      )}

      <FadeUp className="mt-16">
        <Link
          to="/"
          data-testid="news-back-home-link"
          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-mute2 transition-colors hover:text-cyanx"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back home
        </Link>
      </FadeUp>
    </div>
  );
}

export { fmtDate };
export const _arrow = ArrowUpRight;
